import { motion } from "framer-motion";
import { LogOut, Trash2, ChevronRight, User, FileText, Shield, Sun, Moon, AlertTriangle, Download } from "lucide-react";
import { useTheme } from "@/components/ThemeProvider";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";

interface SettingsViewProps {
  onSignOut: () => void;
  isAuthenticated: boolean;
  isGuest: boolean;
  userEmail?: string | null;
  userPhone?: string | null;
}

type ThemeOption = "light" | "dark";

// Format phone number for display (e.g., +1 (555) 123-4567)
const formatPhoneNumber = (phone: string): string => {
  // Remove all non-digit characters except the leading +
  const cleaned = phone.replace(/[^\d+]/g, '');
  
  // Check if it starts with + and has enough digits
  if (cleaned.startsWith('+') && cleaned.length >= 11) {
    const countryCode = cleaned.slice(0, cleaned.length - 10);
    const areaCode = cleaned.slice(-10, -7);
    const firstPart = cleaned.slice(-7, -4);
    const lastPart = cleaned.slice(-4);
    return `${countryCode} (${areaCode}) ${firstPart}-${lastPart}`;
  }
  
  // Return as-is if format doesn't match expected pattern
  return phone;
};

export const SettingsView = ({ 
  onSignOut, 
  isAuthenticated, 
  isGuest,
  userEmail,
  userPhone,
}: SettingsViewProps) => {
  const { theme, setTheme } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const themeOptions: { value: ThemeOption; label: string; icon: React.ReactNode }[] = [
    { value: "light", label: "Light", icon: <Sun className="w-4 h-4" /> },
    { value: "dark", label: "Dark", icon: <Moon className="w-4 h-4" /> },
  ];

  // Determine what to display for account info
  const getAccountDisplay = () => {
    if (isGuest) return "Guest User";
    if (userEmail) return userEmail;
    if (userPhone) return formatPhoneNumber(userPhone);
    return "Metabolic Member";
  };

  const handleSignOut = async () => {
    // Clear all local storage data
    localStorage.clear();
    // Perform Supabase sign out
    await onSignOut();
  };

  const handleDeleteAccount = async () => {
    if (isGuest) {
      // For guests, just sign out
      handleSignOut();
      return;
    }

    setIsDeleting(true);
    try {
      // Call the edge function to delete the account
      const { data, error } = await supabase.functions.invoke('delete-user-account', {
        method: 'POST',
      });

      if (error) {
        console.error('Error deleting account:', error);
        toast({ 
          title: "Error", 
          description: "Failed to delete account. Please try again.", 
          variant: "destructive" 
        });
        setIsDeleting(false);
        return;
      }

      // Clear all local storage
      localStorage.clear();
      
      // Sign out from Supabase
      await supabase.auth.signOut();
      
      toast({ 
        title: "Account Deleted", 
        description: "Your account and all data have been permanently removed." 
      });

      // Navigate to auth page
      navigate('/auth', { replace: true });
      
    } catch (err) {
      console.error('Error deleting account:', err);
      toast({ 
        title: "Error", 
        description: "Failed to delete account. Please try again.", 
        variant: "destructive" 
      });
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  const handleExportData = async () => {
    if (!user || isGuest) {
      toast({
        title: "Export unavailable",
        description: "Data export is only available for registered users.",
        variant: "destructive"
      });
      return;
    }

    setIsExporting(true);
    try {
      // Fetch all user data from relevant tables
      const [profileResult, groceryListsResult, consentLogsResult] = await Promise.all([
        supabase.from('profiles').select('*').eq('user_id', user.id),
        supabase.from('user_grocery_lists').select('*').eq('user_id', user.id),
        supabase.from('consent_logs').select('*').eq('user_id', user.id)
      ]);

      const exportData = {
        exportDate: new Date().toISOString(),
        userId: user.id,
        profile: profileResult.data?.[0] || null,
        groceryLists: groceryListsResult.data || [],
        consentLogs: consentLogsResult.data || [],
        metadata: {
          exportVersion: "1.0",
          dataTypes: ["profile", "grocery_lists", "consent_logs"]
        }
      };

      // Create and download JSON file
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `metabolic-data-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast({
        title: "Data exported",
        description: "Your data has been downloaded as a JSON file."
      });
    } catch (err) {
      console.error('Export error:', err);
      toast({
        title: "Export failed",
        description: "Unable to export your data. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="px-6 py-8 pb-32"
    >
      <h1 className="text-2xl font-semibold text-foreground mb-8">Settings</h1>

      <div className="space-y-4">
        {/* Account Section */}
        {isAuthenticated && (
          <div className="bg-card rounded-2xl border border-border overflow-hidden">
            <div className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-secondary flex items-center justify-center">
                <User className="w-5 h-5 text-metabolic-500" />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="font-medium text-foreground">Account</h3>
                <p className="text-sm text-muted-foreground truncate">
                  {getAccountDisplay()}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Theme Selector */}
        <div className="bg-card rounded-2xl border border-border overflow-hidden">
          <div className="p-4">
            <h3 className="font-medium text-foreground mb-3">Theme</h3>
            <div className="flex bg-secondary rounded-xl p-1">
              {themeOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => setTheme(option.value)}
                  className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                    theme === option.value
                      ? "bg-card text-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {option.icon}
                  <span>{option.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Legal Links */}
        <div className="bg-card rounded-2xl border border-border overflow-hidden">
          <Link 
            to="/privacy" 
            className="w-full p-4 flex items-center justify-between hover:bg-secondary/50 transition-colors border-b border-border"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-secondary flex items-center justify-center">
                <Shield className="w-5 h-5 text-metabolic-500" />
              </div>
              <h3 className="font-medium text-foreground">Privacy Policy</h3>
            </div>
            <ChevronRight className="w-5 h-5 text-muted-foreground" />
          </Link>
          <Link 
            to="/terms" 
            className="w-full p-4 flex items-center justify-between hover:bg-secondary/50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-secondary flex items-center justify-center">
                <FileText className="w-5 h-5 text-metabolic-500" />
              </div>
              <h3 className="font-medium text-foreground">Terms of Service</h3>
            </div>
            <ChevronRight className="w-5 h-5 text-muted-foreground" />
          </Link>
        </div>

        {/* Data Export - GDPR Right to Access */}
        {!isGuest && (
          <div className="bg-card rounded-2xl border border-border overflow-hidden">
            <button
              onClick={handleExportData}
              disabled={isExporting}
              className="w-full p-4 flex items-center justify-between hover:bg-secondary/50 transition-colors disabled:opacity-50"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-secondary flex items-center justify-center">
                  <Download className="w-5 h-5 text-metabolic-500" />
                </div>
                <div className="text-left">
                  <h3 className="font-medium text-foreground">Download My Data</h3>
                  <p className="text-sm text-muted-foreground">
                    {isExporting ? "Preparing export..." : "Export all your data as JSON"}
                  </p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            </button>
          </div>
        )}
        <div className="bg-card rounded-2xl border border-border overflow-hidden">
          <button
            onClick={handleSignOut}
            className="w-full p-4 flex items-center justify-between hover:bg-secondary/50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-secondary flex items-center justify-center">
                <LogOut className="w-5 h-5 text-metabolic-500" />
              </div>
              <div className="text-left">
                <h3 className="font-medium text-foreground">Sign Out</h3>
                <p className="text-sm text-muted-foreground">
                  {isGuest ? "You're signed in as Guest" : "End your session"}
                </p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-muted-foreground" />
          </button>
        </div>

        {/* Danger Zone - Delete Account */}
        {!isGuest && (
          <div className="mt-8">
            <h2 className="text-sm font-medium text-destructive mb-3 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              Danger Zone
            </h2>
            <div className="bg-card rounded-2xl border border-destructive/30 overflow-hidden">
              <button 
                onClick={() => setShowDeleteDialog(true)}
                className="w-full p-4 flex items-center justify-between hover:bg-destructive/10 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-destructive/10 flex items-center justify-center">
                    <Trash2 className="w-5 h-5 text-red-500 dark:text-red-400" />
                  </div>
                  <div className="text-left">
                    <h3 className="font-medium text-red-600 dark:text-red-400">Delete Account</h3>
                    <p className="text-sm text-muted-foreground">Permanently remove all your data</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground" />
              </button>
            </div>
          </div>
        )}
      </div>

      <p className="text-center text-xs text-muted-foreground mt-12">
        Metabolic v1.0.0
      </p>

      {/* Delete Account Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="w-5 h-5" />
              Delete Account?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-left space-y-2">
              <p>
                <strong>This action is permanent and cannot be undone.</strong>
              </p>
              <p>
                All your data will be permanently deleted, including:
              </p>
              <ul className="list-disc list-inside text-sm space-y-1 ml-2">
                <li>Your profile information</li>
                <li>All saved grocery lists</li>
                <li>Your health goal preferences</li>
                <li>Shopping history</li>
              </ul>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel disabled={isDeleting}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteAccount}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? "Deleting..." : "Yes, Delete My Account"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </motion.div>
  );
};
